import { gql } from 'graphql-request';
import fetcher from './fetcher';

export interface PotlatchConfig {
  slug: string;
  json: string;
}

const QUERY = gql`
  query PotlatchConfig($slug: String!) {
    potlatch(slug: $slug) {
      slug
      json
    }
  }
`;

// Site-wide config is the same on every page, so fetch it once per TTL rather
// than on every request. Editors' changes take up to a day to go live (or a
// restart/deploy).
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
// After a failed refresh, wait this long before trying the CMS again.
const RETRY_AFTER_MS = 60_000;

const cache = new Map<
  string,
  { value: PotlatchConfig | null; expiresAt: number }
>();
const inFlight = new Map<string, Promise<PotlatchConfig | null>>();

async function load(slug: string): Promise<PotlatchConfig | null> {
  try {
    const data = await fetcher(QUERY, { slug });
    const value = data?.potlatch ?? null;
    cache.set(slug, { value, expiresAt: Date.now() + CACHE_TTL_MS });
    return value;
  } catch (error) {
    console.error(`Error fetching Potlatch config "${slug}":`, error);
    // Keep serving the last good value for a while so an outage doesn't make
    // every request wait on a failing fetch; with none, retry next call.
    const stale = cache.get(slug);
    if (!stale) return null;
    stale.expiresAt = Date.now() + RETRY_AFTER_MS;
    return stale.value;
  } finally {
    inFlight.delete(slug);
  }
}

/**
 * Fetches a Potlatch config collection by slug, cached in memory for a day.
 * Concurrent callers share one request. Resolves to null (never rejects) if the
 * config can't be loaded, so callers must tolerate it being missing.
 */
export function getPotlatchConfig(
  slug: string
): Promise<PotlatchConfig | null> {
  const cached = cache.get(slug);
  if (cached && cached.expiresAt > Date.now()) {
    return Promise.resolve(cached.value);
  }

  let pending = inFlight.get(slug);
  if (!pending) {
    pending = load(slug);
    inFlight.set(slug, pending);
  }
  return pending;
}

export function clearPotlatchConfigCache(): void {
  cache.clear();
  inFlight.clear();
}

export default getPotlatchConfig;
